import * as $ from 'jquery';
import { IDisposable, Lazy, serialDisposable, IEventAggregator, EventAggregator, disposableSubscription, disposable } from 'common/barrels/core';
import { div, button, icon } from 'common/barrels/dom';
import { AsyncSubject } from 'common/barrels/rx';
import { emptyGuid } from 'common/functions/guid';
import { blockBusy } from 'common/dom/block'

import { IResourceProvider } from 'common/resourceprovider';
import { ErrorReport } from 'runtime/errorreport';
import { MethodRouter } from 'runtime/methodrouter';
import { applyBindings } from 'runtime/bindingengine';
import { WorkItemViewInfo } from 'runtime/data/workitemviewinfo';
import { RequestTracker } from 'runtime/requesttracker';
import { ViewModel } from 'runtime/viewmodel';
import { HostDefunct } from 'runtime/events/hostdefunct';
import { RuntimeInfoRequested } from 'runtime/events/runtimeinforequested';
import { CloseRequested } from './events/closerequested';
import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { RuntimeErrorOccurred } from 'runtime/events/runtimeerroroccurred';
import { RuntimeHostRequested } from 'runtime/events/runtimehostrequested';
import { ViewRequested } from 'runtime/events/viewrequested';
import { RuntimeHostClosed } from 'runtime/events/runtimehostclosed';
import { GenericEvent } from 'runtime/events/genericevent';
import { Serializer } from 'common/serializer';
import { IViewProvider } from 'shared/viewprovider';

import { ActivityExecutionRequested } from 'runtime/events/activityexecutionrequested';
import { OpenWritableWorkItemRequested } from 'runtime/events/openwritableworkitemrequested';
import { WorkItemSubmitted } from 'runtime/events/workitemsubmitted';

import { showSubmittedAlert } from 'runtime/submittedalert';

export class RuntimeHost implements IDisposable {
    private viewDisposables = serialDisposable(null);
    private errorReport: Lazy<ErrorReport>;
    private resources: IResourceProvider;
    private $parent: JQuery;
    private parentEventAggregator: IEventAggregator;
    private serializer: Serializer;
    private viewProvider: IViewProvider;

    constructor(
        $parent: JQuery,
        wivi: WorkItemViewInfo,
        resources: IResourceProvider,
        parentEventAggregator: IEventAggregator,
        serializer: Serializer,
        viewProvider: IViewProvider,
        csrfToken: () => string,
        private submittedViewUrl: (workItemId: string) => string,
        private dialogContainerSelectorOverride?: string
    ) {
        this.$parent = $parent;
        this.resources = resources;
        this.parentEventAggregator = parentEventAggregator;
        this.errorReport = new Lazy<ErrorReport>(() => new ErrorReport(this.resources));
        this.serializer = serializer;
        this.viewProvider = viewProvider;

        this.hostView(wivi, serializer, csrfToken);
    }

    private hostView(wivi: WorkItemViewInfo, serializer: Serializer, csrfToken: () => string) {
        const requests = new RequestTracker();
        const eventAggregator = new EventAggregator();
        eventAggregator.passthrough(this.parentEventAggregator, RuntimeInfoRequested);
        const methods = new MethodRouter(null, wivi.viewName);
        const vm = new ViewModel(eventAggregator, this.resources, methods, wivi, requests, serializer, csrfToken);

        const $hostContainer = div('runtimehost').appendTo(this.$parent).owns(methods);

        const busyDisposable = serialDisposable(null);

        let propagatingGenericEventToParent = false;

        const $contentContainer = div('runtimehost-content ' + (wivi.offerClaim ? 'runtimehost-content-with-claimbar' : 'runtimehost-content-without-claimbar')).appendTo($hostContainer);
        
        if (wivi.offerClaim) {
            const $claimBar = div('runtimehost-claimbar')
                .append(icon('exclamation-triangle'))
                .append(div().text(this.resources.getCoreString('WorkItemClaimOfferText')))
                .append(button('runtimehost-claimbar-dismiss').val(this.resources.getCoreString('WorkItemClaimOfferDismiss')).clicks(o => o.subscribe(x => {
                    $contentContainer.addClass('runtimehost-content-without-claimbar').removeClass('runtimehost-content-with-claimbar');
                    $claimBar.remove();
                })))
                .append(button('runtimehost-claimbar-claim').val(this.resources.getCoreString('WorkItemClaimOfferClaim')).clicks(o => o.subscribe(x => {
                    this.close(true);
                    this.parentEventAggregator.publish(new OpenWritableWorkItemRequested(wivi.workItem.workItemId, null, false));
                })))
                .appendTo($hostContainer);
        }

        this.viewDisposables.set(
            disposableSubscription(
                eventAggregator.query(HostDefunct).subscribe(() => this.close()),
                eventAggregator.query(WorkItemSubmitted).subscribe(x => showSubmittedAlert(this.resources, x, this.submittedViewUrl)),
                eventAggregator.query(CloseRequested).subscribe(() => this.close()),
                eventAggregator.query(Busy).subscribe(x => busyDisposable.set(blockBusy($('.runtime-application-container')))),
                eventAggregator.query(NotBusy).subscribe(() => busyDisposable.set(null)),
                eventAggregator.query(RuntimeErrorOccurred).subscribe(x => this.showError(x)),
                eventAggregator.query(RuntimeHostRequested).subscribe(x => this.hostView(x.wivi, serializer, csrfToken)),
                eventAggregator.query(ViewRequested).subscribe(x => this.viewProvider.getView(eventAggregator, wivi.workItem.definitionName, wivi.workItem.definitionVersion, x.viewName, x.callback)),
                this.getView(eventAggregator, this.viewProvider, wivi.workItem.definitionName, wivi.workItem.definitionVersion, wivi.viewName).subscribe(view => {
                    const $host = $(view).appendTo($contentContainer);
                    applyBindings($host, emptyGuid, eventAggregator, methods, vm.values, this.resources, null, this.serializer);
                    if (this.dialogContainerSelectorOverride)
                        ($host.data('control').setPopupHostOverride(this.dialogContainerSelectorOverride));
                }),
                disposable(() => {
                    vm.dispose();
                    $hostContainer.remove();
                }),
                eventAggregator.query(GenericEvent).subscribe(x => {
                    propagatingGenericEventToParent = true;
                    this.parentEventAggregator.publish(x);
                    propagatingGenericEventToParent = false;
                }),
                this.parentEventAggregator.query(GenericEvent).filter(() => !propagatingGenericEventToParent).subscribe(x => eventAggregator.publish(x)),

                this.parentEventAggregator.query(ActivityExecutionRequested).subscribe(x => eventAggregator.publish(x)),

                busyDisposable
            )
        );
    }

    dispose(): void {
        this.viewDisposables.dispose();
        this.viewProvider.dispose();
    }

    private close(suppressTeardown = false): void {
        this.parentEventAggregator.publish(new RuntimeHostClosed(null, suppressTeardown));
        this.viewDisposables.set(null);
    }

    private showError(messageObject: any) {
        this.errorReport.value().addError(messageObject);
    }

    private getView(events: IEventAggregator, viewProvider: IViewProvider, definitionName: string, definitionVersion: string, viewName: string) {
        const async = new AsyncSubject<string>();

        viewProvider.getView(events, definitionName, definitionVersion, viewName, view => {
            async.next(view);
            async.complete();
        });

        return async.asObservable();
    }
}